import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { resetImageFadeIn } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.refs.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideSelect, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
    resetImageFadeIn(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Handles slide selection to auto-embed video when a video slide becomes active.
   * Loads the iframe without autoplay so the video is visible but not playing.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideSelect = (event) => {
    const { slide } = event.detail;
    if (!slide) return;

    const isVideoSlide =
      slide.classList.contains('product-media-container--video') ||
      slide.classList.contains('product-media-container--external_video');
    if (!isVideoSlide) return;

    const deferredMedia = slide.querySelector('deferred-media');
    if (!deferredMedia || deferredMedia.getAttribute('data-media-loaded')) return;

    const content = deferredMedia.querySelector('template')?.content.firstElementChild?.cloneNode(true);
    if (!content) return;

    // Strip autoplay from iframe src before appending to prevent auto-playing
    if (content instanceof HTMLIFrameElement) {
      content.src = content.src.replace(/autoplay=1/g, 'autoplay=0');
    }

    // Disable autoplay on native video elements
    if (content instanceof HTMLVideoElement) {
      content.autoplay = false;
      content.removeAttribute('autoplay');
    }

    deferredMedia.setAttribute('data-media-loaded', 'true');
    deferredMedia.appendChild(content);
    deferredMedia.toggleAttribute('playing', true);
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
